import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ordersAPI } from '../services/api';
import { HiOutlineEye, HiOutlineShoppingBag } from 'react-icons/hi';

const Orders = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await ordersAPI.getMyOrders();
      setOrders(response.data.orders);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      processing: 'bg-purple-100 text-purple-800',
      shipped: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <section className="bg-primary-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-serif text-4xl md:text-5xl mb-4">{t('orders.myOrders')}</h1>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-luxury-gold transition-colors">Home</Link>
            <span>/</span>
            <Link to="/account" className="hover:text-luxury-gold transition-colors">Account</Link>
            <span>/</span>
            <span>Orders</span>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {orders.length === 0 ? (
            <div className="text-center py-16">
              <HiOutlineShoppingBag className="w-20 h-20 mx-auto text-gray-300 mb-6" />
              <h2 className="font-serif text-2xl mb-4">{t('orders.noOrders')}</h2>
              <p className="text-gray-500 mb-8">{t('orders.noOrdersDesc')}</p>
              <Link
                to="/shop"
                className="inline-block bg-luxury-charcoal text-white px-10 py-4 text-sm uppercase tracking-widest hover:bg-gray-800 transition-colors"
              >
                Start Shopping
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {orders.map((order) => (
                <div key={order._id} className="bg-white border border-gray-100 p-6">
                  {/* Order Header */}
                  <div className="flex flex-wrap items-start justify-between gap-4 pb-4 border-b border-gray-100">
                    <div className="flex flex-wrap gap-6">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">{t('orders.orderNumber')}</p>
                        <p className="font-medium">#{order.orderNumber}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">{t('orders.orderDate')}</p>
                        <p>{new Date(order.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">{t('orders.orderTotal')}</p>
                        <p className="font-medium">{order.total.toFixed(2)} TND</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 text-xs rounded-full capitalize ${getStatusColor(order.status)}`}>
                        {t(`orders.${order.status}`)}
                      </span>
                      <Link
                        to={`/order/${order._id}`}
                        className="flex items-center gap-1 text-sm text-luxury-gold hover:underline"
                      >
                        <HiOutlineEye className="w-4 h-4" />
                        {t('orders.viewDetails')}
                      </Link>
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="pt-4">
                    <div className="flex flex-wrap gap-4">
                      {order.items.slice(0, 4).map((item, index) => (
                        <Link
                          key={index}
                          to={`/product/${item.product?.slug}`}
                          className="w-16 h-20 flex-shrink-0 hover:opacity-80 transition-opacity"
                        >
                          <img
                            src={item.product?.images?.[0] || '/placeholder.jpg'}
                            alt={item.product?.name}
                            className="w-full h-full object-cover"
                          />
                        </Link>
                      ))}
                      {order.items.length > 4 && (
                        <div className="w-16 h-20 flex items-center justify-center bg-gray-100 text-sm text-gray-500">
                          +{order.items.length - 4}
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-3">
                      {order.items.length} {t('orders.items')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Orders;
